import { ErrorHandler, Injectable, inject } from '@angular/core';
import { LoggingService } from './logging.service';
import { AppError } from '../models/error.model';

/**
 * Global error handler service
 * Catches all unhandled errors in the application
 * 
 * Register in app.config.ts:
 * ```
 * providers: [
 *   { provide: ErrorHandler, useClass: GlobalErrorHandlerService }
 * ]
 * ```
 */
@Injectable()
export class GlobalErrorHandlerService implements ErrorHandler {
  private logger = inject(LoggingService);

  handleError(error: Error | AppError): void {
    // Log the error
    this.logger.error('Unhandled error occurred', error);

    // Check if it's our custom AppError
    if (error instanceof AppError) {
      this.handleAppError(error);
    } else {
      this.handleGenericError(error);
    }

    // In development, also throw to show in console with full stack
    if (!this.isProduction()) {
      console.error('🔴 Error:', error);
    }
  }

  /**
   * Handle custom application errors
   */
  private handleAppError(error: AppError): void {
    const userMessage = error.getUserMessage();

    // You could show a toast notification here
    // this.toastService.error(userMessage);

    // Send to analytics/monitoring in production
    if (this.isProduction()) {
      this.sendToMonitoring(error);
    }
  }

  /**
   * Handle generic errors
   */
  private handleGenericError(error: Error): void {
    // Show generic error message to user
    const userMessage = 'An unexpected error occurred. Please try again.';

    // You could show a toast notification here
    // this.toastService.error(userMessage);

    // Send to monitoring
    if (this.isProduction()) {
      this.sendToMonitoring(error);
    }
  }

  /**
   * Send error to monitoring service
   * TODO: Integrate with actual monitoring service (Sentry, etc.)
   */
  private sendToMonitoring(error: Error): void {
    // Example: Send to Sentry
    // Sentry.captureException(error);

    // Or custom endpoint
    // this.http.post('/api/errors', {
    //   message: error.message,
    //   stack: error.stack,
    //   timestamp: new Date().toISOString(),
    //   userAgent: navigator.userAgent,
    // }).subscribe();
  }

  /**
   * Check if running in production
   */
  private isProduction(): boolean {
    return typeof window !== 'undefined' && window.location.hostname !== 'localhost';
  }
}


